<script setup lang="ts">
import { onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, type RouteLocationNormalized } from "vue-router";

// Shows a bar while a route's async chunk loads. Mounted once per portal.

// Matches the `[data-placeholder]` quiet window in style.css.
const SHOW_AFTER_MS = 200;
// Under that window, so the bar is gone before the next screen's skeletons show.
const DONE_MS = 150;

const { t } = useI18n();
const router = useRouter();

const active = ref(false);
// Runs the bar to the right edge before it hides.
const done = ref(false);
let showTimer: ReturnType<typeof setTimeout> | null = null;
let doneTimer: ReturnType<typeof setTimeout> | null = null;
// afterEach also reports a cancelled older navigation, which must not clear the bar.
let tracking: RouteLocationNormalized | null = null;

function clearShowTimer() {
  if (showTimer) clearTimeout(showTimer);
  showTimer = null;
}

function finish(to: RouteLocationNormalized) {
  if (to !== tracking) return;
  tracking = null;
  clearShowTimer();
  // A navigation that beat the show window must not flash a completed bar.
  if (!active.value) return;
  active.value = false;
  done.value = true;
  if (doneTimer) clearTimeout(doneTimer);
  doneTimer = setTimeout(() => (done.value = false), DONE_MS);
}

const removers = [
  router.beforeEach((to, from) => {
    // A query-only change re-uses the mounted component and loads nothing.
    if (to.path === from.path) return;
    tracking = to;
    clearShowTimer();
    showTimer = setTimeout(() => (active.value = true), SHOW_AFTER_MS);
  }),
  // A navigation that throws (a failed chunk load) skips afterEach.
  router.afterEach((to) => finish(to)),
  router.onError((_error, to) => finish(to)),
];

onUnmounted(() => {
  removers.forEach((remove) => remove());
  clearShowTimer();
  if (doneTimer) clearTimeout(doneTimer);
});
</script>

<template>
  <Transition
    enter-active-class="transition-opacity duration-150 motion-reduce:transition-none"
    leave-active-class="transition-opacity duration-150 motion-reduce:transition-none"
  >
    <div
      v-if="active || done"
      class="pointer-events-none fixed inset-x-0 top-0 z-[100] h-[3px]"
      role="status"
      aria-live="polite"
    >
      <span class="sr-only">{{ t("common.loadingResults") }}</span>
      <div class="route-progress h-full bg-primary" :class="{ 'route-progress-done': done }" aria-hidden="true" />
    </div>
  </Transition>
</template>

<style scoped>
/* Never reaches the edge, since progress is unknown. */
.route-progress {
  animation: route-progress 8s cubic-bezier(0.2, 0.7, 0.3, 1) forwards;
  transform-origin: left;
}

@keyframes route-progress {
  0% {
    transform: scaleX(0.02);
  }
  15% {
    transform: scaleX(0.6);
  }
  100% {
    transform: scaleX(0.95);
  }
}

.route-progress-done {
  animation: none;
  transform: scaleX(1);
  transition: transform 150ms ease-out;
}

/* Neutral under reduced motion: a flash of brand red reads as an alert. */
@media (prefers-reduced-motion: reduce) {
  .route-progress {
    animation: none;
    transform: scaleX(1);
    background-color: hsl(var(--foreground) / 0.4);
  }
}
</style>
